/**
 * Pure date helpers for the M.1 DateSelector. No React, no DOM.
 * Tests should target this file directly.
 *
 * Business timezone: UTC+5 (Orenburg). All formatting here is local-time;
 * the API layer converts to/from "YYYY-MM-DD" strings.
 */

/**
 * Genitive-case month abbreviations: "27 мая · ср", "1 июня · пн".
 * Russian genitive — май → мая, июнь → июня, июль → июля — is non-trivially
 * different from nominative for the summer months; the others stay as their
 * conventional 3-letter abbreviations which read as genitive when paired
 * with a day number.
 */
export const MONTHS_GENITIVE = [
  "янв", "фев", "мар", "апр", "мая", "июня",
  "июля", "авг", "сен", "окт", "ноя", "дек",
] as const;

export const MONTHS_FULL = [
  "ЯНВАРЬ", "ФЕВРАЛЬ", "МАРТ", "АПРЕЛЬ", "МАЙ", "ИЮНЬ",
  "ИЮЛЬ", "АВГУСТ", "СЕНТЯБРЬ", "ОКТЯБРЬ", "НОЯБРЬ", "ДЕКАБРЬ",
] as const;

/** ISO weekday order — пн = 0, вс = 6 (matches our day-grid layout). */
export const WEEKDAYS_ISO = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"] as const;

/** JS getDay() returns 0=Sun..6=Sat — convert to ISO 0=Mon..6=Sun. */
export function toIsoWeekday(d: Date): number {
  const dow = d.getDay();
  return dow === 0 ? 6 : dow - 1;
}

export function isWeekend(d: Date): boolean {
  const iso = toIsoWeekday(d);
  return iso >= 5; // sat or sun
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** "27 май · ср" — compact label for the trigger center. */
export function fmtDayLabel(d: Date): { date: string; weekday: string } {
  const day = d.getDate();
  const mon = MONTHS_GENITIVE[d.getMonth()] ?? "?";
  const wd = WEEKDAYS_ISO[toIsoWeekday(d)] ?? "?";
  return { date: `${day} ${mon}`, weekday: wd };
}

/** "27 май - ?" or "27-29 мая" — range label for the trigger. */
export function fmtRangeLabel(start: Date | null, end: Date | null): string {
  if (!start) return "";
  const sd = start.getDate();
  const sm = MONTHS_GENITIVE[start.getMonth()] ?? "?";
  if (!end) return `${sd} ${sm} - ?`;
  const ed = end.getDate();
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${sd}-${ed} ${sm}`;
  }
  const em = MONTHS_GENITIVE[end.getMonth()] ?? "?";
  return `${sd} ${sm} – ${ed} ${em}`;
}

/** "МАЙ 2026" — popover header. */
export function fmtMonthHeader(d: Date): string {
  return `${MONTHS_FULL[d.getMonth()] ?? "?"} ${d.getFullYear()}`;
}

/**
 * Build a 6×7 matrix of days for the popover.
 * First cell aligns to ISO weekday (Monday); fills with neighbouring month
 * dates so the grid is always exactly 42 cells.
 */
export interface DayCell {
  date: Date;
  inMonth: boolean;
}

export function getMonthMatrix(viewMonth: Date): DayCell[] {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = toIsoWeekday(firstOfMonth); // 0..6 → number of leading cells
  const gridStart = addDays(firstOfMonth, -startOffset);

  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    cells.push({ date: d, inMonth: d.getMonth() === month });
  }
  return cells;
}

/** Inclusive check whether `d` is inside [start, end] (any order). */
export function inRange(d: Date, start: Date, end: Date): boolean {
  const t = startOfDay(d).getTime();
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();
  return t >= Math.min(s, e) && t <= Math.max(s, e);
}

/** Coerce wire format "YYYY-MM-DD" → Date or null. */
export function parseIsoDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Date → wire format "YYYY-MM-DD" used by the API. */
export function fmtIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
